import { useId, useState } from 'react';
import { Button } from '@progress/kendo-react-buttons';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import type { TenantDeleteResponse, TenantDto } from '@shared/api-types';
import { adminApiFetch } from '../../lib/adminApiClient';

// Tenant soft delete (AAI-151), mirroring ServiceAI's Manage "Delete tenant": the
// tenant must be Suspended first, the admin types its exact name, and the result
// lists any warnings. Nothing is dropped — the database is renamed XXXDELETE-….

const TENANT_STATUS_SUSPENDED = 2;

interface Props {
  tenant: TenantDto;
  /** Called with the tombstoned tenant once the delete has gone through. */
  onDeleted: (tenant: TenantDto) => void;
}

export function DeleteTenantPanel({ tenant, onDeleted }: Props) {
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<TenantDeleteResponse | null>(null);
  const inputId = useId();

  const suspended = tenant.status === TENANT_STATUS_SUSPENDED;
  const nameMatches = typed.trim() === tenant.name.trim();

  const close = () => {
    setOpen(false);
    setTyped('');
    setError(null);
  };

  const confirm = async () => {
    setDeleting(true);
    setError(null);
    try {
      const resp = await adminApiFetch<TenantDeleteResponse>(`/api/admin/tenants/${tenant.id}/delete`, {
        method: 'POST',
        body: { confirmName: typed },
      });
      setOpen(false);
      setTyped('');
      setResult(resp);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div style={{ ...panelStyle, marginTop: 16 }} data-testid="delete-tenant-panel">
      <div style={{ fontSize: 13, fontWeight: 600, color: '#475569' }}>Delete tenant</div>
      <div style={{ color: '#64748b', fontSize: 13 }}>
        Removes the tenant&rsquo;s access and renames its database to <code>XXXDELETE-…</code>. The
        database is kept, not dropped. {suspended ? '' : 'Suspend the tenant first, then delete it.'}
      </div>
      <div>
        <Button
          themeColor="error"
          disabled={!suspended}
          title={suspended ? 'Delete this tenant' : 'Suspend the tenant first, then delete it'}
          onClick={() => setOpen(true)}
          data-testid="delete-tenant-btn"
        >
          Delete tenant…
        </Button>
      </div>

      {open && (
        <Dialog title="Confirm tenant deletion" onClose={close} width={480}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, fontSize: 13 }}>
            <div>
              <strong>This will:</strong>
              <ul style={{ margin: '6px 0 0', paddingLeft: 20 }}>
                <li>Mark the tenant deleted</li>
                <li>Remove every user&rsquo;s access to it (the users themselves are kept)</li>
                <li>Remove its runners, API keys, schedules and Teams connections</li>
                <li>Clear its Zoho subscription link</li>
                <li>Rename its database to <code>XXXDELETE-{tenant.tenantDatabaseName}</code> (not dropped)</li>
              </ul>
            </div>
            <div style={{ color: '#b45309' }}>
              The customer&rsquo;s runner apps in their Azure subscription keep running and will fail to
              authenticate. Ask the customer to remove that resource group.
            </div>
            <label htmlFor={inputId}>
              Type the tenant name <strong>{tenant.name}</strong> to confirm:
            </label>
            <input
              id={inputId}
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              autoComplete="off"
              style={inputStyle}
              data-testid="delete-confirm-input"
            />
            {error && <div style={{ color: '#dc2626' }}>{error}</div>}
          </div>
          <DialogActionsBar>
            <Button onClick={close} disabled={deleting}>Cancel</Button>
            <Button
              themeColor="error"
              disabled={!nameMatches || deleting}
              onClick={() => void confirm()}
              data-testid="delete-confirm-btn"
            >
              {deleting ? 'Deleting…' : 'Delete tenant'}
            </Button>
          </DialogActionsBar>
        </Dialog>
      )}

      {result && (
        <Dialog
          title={result.warnings.length > 0 ? 'Deletion completed with warnings' : 'Tenant deleted'}
          onClose={() => { onDeleted(result.tenant); setResult(null); }}
          width={480}
        >
          <div style={{ fontSize: 13 }} data-testid="delete-result">
            {result.warnings.length > 0 ? (
              <>
                <p style={{ marginTop: 0 }}>The tenant was deleted, but these steps need attention:</p>
                <ul style={{ paddingLeft: 20 }}>
                  {result.warnings.map((w) => <li key={w}>{w}</li>)}
                </ul>
              </>
            ) : (
              <p style={{ marginTop: 0 }}>
                &ldquo;{result.tenant.name}&rdquo; was deleted. Its database is now{' '}
                <code>{result.tenant.tenantDatabaseName}</code>.
              </p>
            )}
          </div>
          <DialogActionsBar>
            <Button themeColor="primary" onClick={() => { onDeleted(result.tenant); setResult(null); }}>
              OK
            </Button>
          </DialogActionsBar>
        </Dialog>
      )}
    </div>
  );
}

const panelStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 14,
  padding: 24,
  background: '#fff',
  borderRadius: 12,
  boxShadow: '0 2px 8px rgba(15, 23, 42, 0.06)',
};

const inputStyle: React.CSSProperties = {
  padding: 10,
  border: '1px solid #cbd5e1',
  borderRadius: 8,
  fontSize: 14,
};
